// GENERATED by tools/lexgen/emit-ts.mjs — DO NOT EDIT.
// Source of truth: lexicons/. Regenerate with `pnpm lex:gen`.

export type Bytes = { $bytes: string };

export type Geo = {
  cell: string;
  path: string[];
  accuracyM?: number;
  precise?: SealedNeedToKnow;
};

export type SealedBroadcast = { ciphertext: Bytes; keyRef: string };
export type SealedNeedToKnow = { ciphertext: Bytes; recipients: string[] };

export type Event<P> = {
  id: string;
  $type: string;
  identityId: string;
  signingKey: string;
  sig: Bytes;
  createdAt: string;
  geo: Geo;
  subject?: string;
  payload: P;
};

export const AccountsBasisValues = ["subscribers", "emitters", "registered"] as const;
export type AccountsBasis = (typeof AccountsBasisValues)[number];
export const AlertSeverityValues = ["info", "warning", "critical"] as const;
export type AlertSeverity = (typeof AlertSeverityValues)[number];
export const BucketLevelValues = ["off", "info", "warning", "critical"] as const;
export type BucketLevel = (typeof BucketLevelValues)[number];
export const CategoryValues = ["Geo", "Met", "Safety", "Security", "Rescue", "Fire", "Health", "Env", "Transport", "Infra", "CBRNE", "Other"] as const;
export type Category = (typeof CategoryValues)[number];
export const CertaintyValues = ["Observed", "Likely", "Possible", "Unlikely", "Unknown"] as const;
export type Certainty = (typeof CertaintyValues)[number];
export const ConsentPurposeValues = ["alerts", "helperAvailability", "preciseLocationRelease", "publicMetrics"] as const;
export type ConsentPurpose = (typeof ConsentPurposeValues)[number];
export const FlagReasonValues = ["falseReport", "spam", "harassment", "dangerousAdvice", "harvestAttempt", "doxxing", "illegal", "coordinatedInauthentic", "other"] as const;
export type FlagReason = (typeof FlagReasonValues)[number];
export const IncidentStatusValues = ["open", "sustained", "closing", "closed"] as const;
export type IncidentStatus = (typeof IncidentStatusValues)[number];
export const LabelReasonValues = ["falseReport", "spam", "harassment", "dangerousAdvice", "harvestAttempt", "doxxing", "illegal", "coordinatedInauthentic"] as const;
export type LabelReason = (typeof LabelReasonValues)[number];
export const NeedTypeValues = ["medical", "rescue", "transport", "shelter", "supplies", "hands", "other"] as const;
export type NeedType = (typeof NeedTypeValues)[number];
export const ResponseTypeValues = ["Shelter", "Evacuate", "Prepare", "Execute", "Avoid", "Monitor", "Assess", "AllClear", "None"] as const;
export type ResponseType = (typeof ResponseTypeValues)[number];
export const RevocationReasonValues = ["expired", "abuse", "error", "keyCompromise"] as const;
export type RevocationReason = (typeof RevocationReasonValues)[number];
export const RoleValues = ["responder", "ngo", "municipality", "moderator", "datasource"] as const;
export type Role = (typeof RoleValues)[number];
export const SeverityValues = ["Extreme", "Severe", "Moderate", "Minor", "Unknown"] as const;
export type Severity = (typeof SeverityValues)[number];
export const SpreadStatusValues = ["moving", "stationary", "dissipating"] as const;
export type SpreadStatus = (typeof SpreadStatusValues)[number];
export const TransitionValues = ["open", "in_progress", "en_route", "arrived", "partial", "done", "cancelled", "reopened"] as const;
export type Transition = (typeof TransitionValues)[number];
export const UrgencyValues = ["Immediate", "Expected", "Future", "Past", "Unknown"] as const;
export type Urgency = (typeof UrgencyValues)[number];

// app.aid.account.contribution
export interface AppAidAccountContribution {
  "beneficiary": string;
  "rail": string;
  "nativeAmount": string;
  "nativeUnit": string;
  "pegValue": number;
  "pegCurrency": string;
  "hostValue": number;
  "hostCurrency": string;
  "hostValueApprox": boolean;
  "fxRate"?: number;
  "fxAt"?: string;
  "observedAt": string;
}

// app.aid.account.cost
export interface AppAidAccountCost {
  "period": { "start": string; "end": string };
  "spendFiat": number;
  "fiatCurrency": string;
  "activeAccounts": number;
  "accountsBasis": string;
  "breakdown"?: { "category": string; "amount": number; "oneTime": boolean }[];
}

// app.aid.account.supporter
export interface AppAidAccountSupporter {
  "active": boolean;
  "since": string;
}

// app.aid.alert.notice
export interface AppAidAlertNotice {
  "subject": string;
  "severity": string;
  "kind": string;
  "distanceKm"?: number;
  "updatedAt": string;
}

// app.aid.alert.subscription
export interface AppAidAlertSubscription {
  "cells": string[];
  "minSeverity": string;
  "push": { "endpoint": string; "p256dh": string; "auth": string };
  "quietHours"?: { "start": string; "end": string; "tz": string };
  "bucketLevels"?: { "bucket": string; "level": string }[];
  "snoozeUntil"?: string;
  "locale": string;
}

// app.aid.helpRequest.accept
export interface AppAidHelpRequestAccept {
  "offer": string;
  "locationGrant"?: SealedNeedToKnow;
}

// app.aid.helpRequest.amend
export interface AppAidHelpRequestAmend {
  "need"?: string;
  "headline"?: SealedBroadcast;
  "details"?: SealedBroadcast;
  "urgency"?: number;
  "people"?: number;
  "window"?: { "from"?: string; "until"?: string };
}

// app.aid.helpRequest.create
export interface AppAidHelpRequestCreate {
  "need": string;
  "headline": SealedBroadcast;
  "details"?: SealedBroadcast;
  "urgency": number;
  "people"?: number;
  "window"?: { "from"?: string; "until"?: string };
}

// app.aid.helpRequest.note
export interface AppAidHelpRequestNote {
  "body": SealedNeedToKnow;
}

// app.aid.helpRequest.offer
export interface AppAidHelpRequestOffer {
  "tags"?: string[];
  "note"?: SealedBroadcast;
  "etaMinutes"?: number;
}

// app.aid.helpRequest.transition
export interface AppAidHelpRequestTransition {
  "to": string;
  "reason"?: SealedBroadcast;
}

// app.aid.helper.availability
export interface AppAidHelperAvailability {
  "tags": string[];
  "cells": string[];
  "until": string;
  "autoAccept"?: boolean;
}

// app.aid.helper.profile
export interface AppAidHelperProfile {
  "displayName"?: string;
  "displayNameSealed"?: SealedBroadcast;
  "skills"?: SealedBroadcast;
  "langs"?: string[];
}

// app.aid.identity.declareKeys
export interface AppAidIdentityDeclareKeys {
  "encryptionKey": string;
}

// app.aid.identity.rotateKey
export interface AppAidIdentityRotateKey {
  "newKey": string;
  "newEncryptionKey"?: string;
  "prev"?: string;
}

// app.aid.incident.closure
export interface AppAidIncidentClosure {
  "classification": { "category": string; "event"?: string };
  "reason": string;
}

// app.aid.incident.snapshot
export interface AppAidIncidentSnapshot {
  "subject": string;
  "classification": { "category": string; "event"?: string; "eventCode"?: string; "severity"?: string; "urgency"?: string; "certainty"?: string; "responseType"?: string };
  "status": string;
  "W": number;
  "confidence": number;
  "cells": string[];
  "footprint"?: string[];
  "source": string;
  "distinctIdentities": number;
  "openedAt"?: string;
  "lastConfirmedAt"?: string;
  "currentFrpMw"?: number;
  "peakFrpMw"?: number;
  "trend"?: { "intensity"?: string; "footprint"?: string; "corroboration"?: string; "worsening"?: boolean };
  "contributors"?: { "feed"?: string; "identity"?: string; "itemId"?: string }[];
  "closedReason"?: string;
}

// app.aid.match.subscribe
export interface AppAidMatchSubscribe {
  "issuedAt": string;
}

// app.aid.match.suggestion
export interface AppAidMatchSuggestion {
  "subject": string;
  "need": string;
  "urgency": number;
  "rank"?: number;
  "cells": string[];
  "updatedAt": string;
}

// app.aid.message
export interface AppAidMessage {
  "text": SealedBroadcast;
  "lang"?: string;
  "replyTo"?: string;
}

// app.aid.mod.appeal
export interface AppAidModAppeal {
  "action": string;
  "statement": SealedBroadcast;
}

// app.aid.mod.flag
export interface AppAidModFlag {
  "subject": string;
  "reason": string;
  "note"?: SealedBroadcast;
}

// app.aid.privacy.consent
export interface AppAidPrivacyConsent {
  "purpose": string;
  "action": string;
  "policyVersion": string;
}

// app.aid.privacy.shred
export interface AppAidPrivacyShred {
  "scope": string;
  "target": string;
}

// app.aid.report
export interface AppAidReport {
  "classification": { "category": string; "event"?: string; "eventCode"?: string; "severity"?: string; "urgency"?: string; "certainty"?: string; "responseType"?: string };
  "severity": number;
  "note"?: SealedBroadcast;
  "observedAt"?: string;
  "provenance"?: { "feed": string; "itemId": string; "fetchedAt": string };
  "resources"?: { "ground"?: number; "aerial"?: number; "vehicles"?: number };
  "frpMw"?: number;
  "footprint"?: string[];
  "media"?: unknown[];
}

// app.aid.spread.estimate
export interface AppAidSpreadEstimate {
  "subject": string;
  "status": string;
  "bearingDeg"?: number;
  "speedKmh"?: number;
  "windBearingDeg"?: number;
  "windSpeedKmh"?: number;
  "windConeCells"?: string[];
  "confidence": number;
  "horizonMinutes": number;
  "pathCells": string[];
}

// app.aid.trust.credential
export interface AppAidTrustCredential {
  "subject": string;
  "role": string;
  "expiresAt": string;
}

// app.aid.trust.label
export interface AppAidTrustLabel {
  "subject": string;
  "weight": number;
  "reason": string;
  "expiresAt"?: string;
}

// app.aid.trust.revocation
export interface AppAidTrustRevocation {
  "credential": string;
  "reason"?: string;
}

// app.aid.trust.vouch
export interface AppAidTrustVouch {
  "subject": string;
}

// app.aid.weather.conditions
export interface AppAidWeatherConditions {
  "cell": string;
  "gridRes"?: number;
  "subject"?: string;
  "windFromDeg": number;
  "speedKmh": number;
  "gustKmh"?: number;
  "observedAt": string;
  "sampledAt": string;
  "source": string;
}

export const KNOWN_TYPES: readonly string[] = [
  "app.aid.account.contribution",
  "app.aid.account.cost",
  "app.aid.account.supporter",
  "app.aid.alert.notice",
  "app.aid.alert.subscription",
  "app.aid.helpRequest.accept",
  "app.aid.helpRequest.amend",
  "app.aid.helpRequest.create",
  "app.aid.helpRequest.note",
  "app.aid.helpRequest.offer",
  "app.aid.helpRequest.transition",
  "app.aid.helper.availability",
  "app.aid.helper.profile",
  "app.aid.identity.declareKeys",
  "app.aid.identity.rotateKey",
  "app.aid.incident.closure",
  "app.aid.incident.snapshot",
  "app.aid.match.subscribe",
  "app.aid.match.suggestion",
  "app.aid.message",
  "app.aid.mod.appeal",
  "app.aid.mod.flag",
  "app.aid.privacy.consent",
  "app.aid.privacy.shred",
  "app.aid.report",
  "app.aid.spread.estimate",
  "app.aid.trust.credential",
  "app.aid.trust.label",
  "app.aid.trust.revocation",
  "app.aid.trust.vouch",
  "app.aid.weather.conditions",
];

export const MANIFEST: Record<string, { class: string; owner: string }> = {
  "app.aid.account.contribution": { class: "public", owner: "core" },
  "app.aid.account.cost": { class: "public", owner: "core" },
  "app.aid.account.supporter": { class: "private", owner: "core" },
  "app.aid.alert.notice": { class: "private", owner: "alerting" },
  "app.aid.alert.subscription": { class: "private", owner: "alerting" },
  "app.aid.defs": { class: "public", owner: "core" },
  "app.aid.helpRequest.accept": { class: "public", owner: "core" },
  "app.aid.helpRequest.amend": { class: "public", owner: "core" },
  "app.aid.helpRequest.create": { class: "public", owner: "core" },
  "app.aid.helpRequest.note": { class: "public", owner: "core" },
  "app.aid.helpRequest.offer": { class: "public", owner: "core" },
  "app.aid.helpRequest.transition": { class: "public", owner: "core" },
  "app.aid.helper.availability": { class: "private", owner: "help" },
  "app.aid.helper.profile": { class: "private", owner: "help" },
  "app.aid.identity.declareKeys": { class: "public", owner: "trust" },
  "app.aid.identity.rotateKey": { class: "public", owner: "trust" },
  "app.aid.incident.closure": { class: "public", owner: "core" },
  "app.aid.incident.snapshot": { class: "public", owner: "projectors" },
  "app.aid.match.subscribe": { class: "private", owner: "help" },
  "app.aid.match.suggestion": { class: "private", owner: "help" },
  "app.aid.message": { class: "public", owner: "core" },
  "app.aid.mod.appeal": { class: "private", owner: "moderation" },
  "app.aid.mod.flag": { class: "private", owner: "moderation" },
  "app.aid.privacy.consent": { class: "public", owner: "privacy" },
  "app.aid.privacy.shred": { class: "public", owner: "privacy" },
  "app.aid.report": { class: "public", owner: "core" },
  "app.aid.spread.estimate": { class: "public", owner: "projectors" },
  "app.aid.trust.credential": { class: "public", owner: "trust" },
  "app.aid.trust.label": { class: "public", owner: "trust" },
  "app.aid.trust.revocation": { class: "public", owner: "trust" },
  "app.aid.trust.vouch": { class: "public", owner: "trust" },
  "app.aid.weather.conditions": { class: "public", owner: "core" },
};

export const FLOAT_PATHS: Record<string, readonly string[]> = {
  "app.aid.account.contribution": ["payload.pegValue", "payload.hostValue", "payload.fxRate"],
  "app.aid.account.cost": ["payload.spendFiat", "payload.breakdown[].amount"],
  "app.aid.alert.notice": ["payload.distanceKm"],
  "app.aid.incident.snapshot": ["payload.W", "payload.confidence", "payload.currentFrpMw", "payload.peakFrpMw"],
  "app.aid.match.suggestion": ["payload.rank"],
  "app.aid.report": ["payload.frpMw"],
  "app.aid.spread.estimate": ["payload.bearingDeg", "payload.speedKmh", "payload.windBearingDeg", "payload.windSpeedKmh", "payload.confidence"],
  "app.aid.trust.label": ["payload.weight"],
  "app.aid.weather.conditions": ["payload.windFromDeg", "payload.speedKmh", "payload.gustKmh"],
};
